import { fromJS, Map } from 'immutable';
import { listToMap } from 'flow/utils';
import Actions from 'flow/actions';

import { withAvatarVersion } from 'flow/utils/avatar-utils';
import { getThirdPartyContactKey } from 'flow/utils/extension-utils';
import { ContactTypeToIndex } from '../utils/contact-utils';

const initialState = Map();

export const contactsReducer = (state = initialState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE: {
            return initialState;
        }
        case Actions.RESULT_CONTACTS: {
            const newContacts = listToMap(fromJS(action.data), (e) =>
                e.get('key'),
            );
            return state.update('externalContacts', Map(), (oldContacts) =>
                oldContacts.mergeDeep(newContacts),
            );
        }
        case Actions.RESULT_SOCIAL_INTEGRATION_CONTACTS: {
            const socialIntegrationContacts = listToMap(
                fromJS(action.data),
                getThirdPartyContactKey,
            );
            return state.set(
                'socialIntegrationContacts',
                socialIntegrationContacts,
            );
        }
        case Actions.RESULT_CONTACT: {
            const fetchedContact = fromJS(action.data);
            const typeIndex = ContactTypeToIndex.get(
                fetchedContact.get('type'),
                'flowContacts',
            );
            // Without the version the contact card keeps a changed picture.
            // Only colleagues: the other writers to externalContacts store
            // the plain address, and the two would keep replacing each other.
            const contactDTO =
                typeIndex === 'flowContacts'
                    ? withAvatarVersion(fetchedContact)
                    : fetchedContact;
            const path = [typeIndex, contactDTO.get('key')];
            // Home leaves avatarResource out when there is no picture, and a
            // merge would keep the old one.
            const withoutRemovedAvatar = contactDTO.get('avatarResource')
                ? state
                : state.deleteIn([...path, 'avatarResource']);
            return withoutRemovedAvatar.mergeIn(path, contactDTO);
        }
        case Actions.RESULT_EDITABLE_CONTACT: {
            const editableContactDTO = fromJS(action.data);
            const typeIndex = ContactTypeToIndex.get(
                editableContactDTO.get('type'),
                'flowContacts',
            );
            return state
                .mergeIn(
                    [
                        typeIndex,
                        editableContactDTO.get('key'),
                        'editableContactDTO',
                    ],
                    editableContactDTO,
                )
                .setIn(
                    [typeIndex, editableContactDTO.get('key'), 'key'],
                    editableContactDTO.get('key'),
                );
        }
        case Actions.RESULT_CONTACT_AVATAR_UPDATE: {
            const avatarResource = action.data.avatarResource
                ? `${action.data.avatarResource}?${Date.now()}`
                : action.data.avatarResource;
            const contactKey = action.data.key;
            const typeIndex =
                state.findKey((contactList) => contactList.has(contactKey)) ||
                'flowContacts';
            return state
                .updateIn([typeIndex, contactKey, 'avatarResource'], () =>
                    fromJS(avatarResource),
                )
                .updateIn(
                    [
                        typeIndex,
                        contactKey,
                        'editableContactDTO',
                        'avatarResource',
                    ],
                    () => fromJS(avatarResource),
                );
        }
        case Actions.RESULT_CONTACT_AVATAR_DELETE: {
            const contactKey = action.data.key;
            const typeIndex = state.findKey((contactList) =>
                contactList.has(contactKey),
            );
            return state
                .deleteIn([typeIndex, contactKey, 'avatarResource'])
                .deleteIn([
                    typeIndex,
                    contactKey,
                    'editableContactDTO',
                    'avatarResource',
                ]);
        }
        case Actions.REMOVE_EXTERNAL_CONTACT:
            return state.deleteIn(['externalContacts', action.key]);
        case Actions.RESULT_NEW_EXTERNAL_CONTACT: {
            const contact = action.data;
            return state.setIn(
                ['externalContacts', contact.key],
                fromJS(contact),
            );
        }
        case Actions.RESULT_UPDATED_CONTACT: {
            // A plain ContactDTO; keep the editable DTO already in the store
            // (the ticket contact card renders its fields from it), with its
            // type synced so a later edit doesn't undo a shared toggle.
            const contact = fromJS(action.data);
            return state.updateIn(
                ['externalContacts', contact.get('key')],
                (existing) => {
                    const editable = existing?.get('editableContactDTO');
                    return editable
                        ? contact.set(
                              'editableContactDTO',
                              editable.set('type', contact.get('type')),
                          )
                        : contact;
                },
            );
        }
        case Actions.RESULT_NUMBER_LOOKUP: {
            const response = fromJS(action.data);

            // Prevent contact props warnings. We always want to store the result to prevent more lookup fetches.
            const dummyContactIfNoLookupDataWasFound = Map({
                key: 'contact-0',
            });
            const newLookup =
                (response && response.get(0)) ||
                dummyContactIfNoLookupDataWasFound;

            const newState = state.updateIn(
                ['cachedLookups', action.number],
                (prevLookup = Map()) => {
                    return prevLookup.merge(newLookup);
                },
            );
            return newState;
        }

        default: {
            return state;
        }
    }
};

export default contactsReducer;
